import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import AuthModal from './AuthModal';
import './App.css';
import { hasSupabaseConfig, supabase } from './supabaseClient';

const lockImage = require('./lock.png');

const getAvatarInitial = (value) => {
  if (!value) return 'U';
  return value.trim().charAt(0).toUpperCase() || 'U';
};

const getAvailabilityLabel = (preference) => {
  if (preference === 'none') return 'Not accepting requests';
  if (preference === 'male') return 'Accepting males only';
  if (preference === 'female') return 'Accepting females only';
  return 'Accepting everyone';
};

const getGenderIndicator = (gender) => {
  const normalizedGender = String(gender || '').trim().toLowerCase();

  if (normalizedGender === 'female') {
    return { symbol: '\u2640', label: 'Female', className: 'public-profile-gender-female' };
  }

  if (normalizedGender === 'male') {
    return { symbol: '\u2642', label: 'Male', className: 'public-profile-gender-male' };
  }

  return null;
};

const buildRatingLabel = (profileData) => {
  if (!profileData) return 'No ratings yet';

  const ratingCount = Number(profileData.rating_count || 0);
  const normalizedRating = Number(
    profileData.user_rating || (ratingCount > 0 ? Number(profileData.total_rating || 0) / ratingCount : 0)
  );

  if (ratingCount <= 0 || !Number.isFinite(normalizedRating)) {
    return 'No ratings yet';
  }

  return `${normalizedRating.toFixed(1)} stars from ${ratingCount} ratings`;
};

const isAbortError = (error) => {
  if (!error) return false;
  if (error.name === 'AbortError') return true;
  if (typeof error.message === 'string' && error.message.toLowerCase().includes('aborted')) return true;
  return false;
};

const isProfileAccessError = (error) => {
  if (!error) return false;
  if (typeof error.code === 'string' && ['42501', 'PGRST301', 'PGRST302'].includes(error.code)) return true;
  const message = String(error.message || '').toLowerCase();
  return (
    message.includes('row-level security') ||
    message.includes('permission denied') ||
    message.includes('not authorized') ||
    message.includes('jwt')
  );
};

const createFallbackPublicProfile = (targetUsername) => ({
  id: `public-${targetUsername}`,
  username: targetUsername,
  avatar_url: null,
  gender: null,
  user_rate: 100,
  chat_preference: 'both',
  user_rating: null,
  total_rating: 0,
  rating_count: 0,
});

function PublicProfilePage() {
  const navigate = useNavigate();
  const { username: rawUsername = '' } = useParams();
  const [profileData, setProfileData] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [profileError, setProfileError] = useState('');
  const [viewerSession, setViewerSession] = useState(null);
  const [checkingViewerSession, setCheckingViewerSession] = useState(true);
  const [showUnlockGate, setShowUnlockGate] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState('');

  const username = useMemo(() => {
    try {
      const decoded = decodeURIComponent(rawUsername).trim();
      return decoded.replace(/^@+/, '');
    } catch (_decodeError) {
      return String(rawUsername || '')
        .trim()
        .replace(/^@+/, '');
    }
  }, [rawUsername]);

  const ratingLabel = useMemo(() => buildRatingLabel(profileData), [profileData]);

  const profileShareUrl = useMemo(() => {
    if (typeof window === 'undefined' || !profileData?.username) return '';
    return `${window.location.origin}/u/${encodeURIComponent(profileData.username)}`;
  }, [profileData?.username]);

  const goToUnlockFlow = useCallback(() => {
    if (!profileData?.username) return;
    navigate(`/app?unlock=${encodeURIComponent(profileData.username)}`);
  }, [navigate, profileData?.username]);

  const handleUnlockClick = useCallback(() => {
    if (!profileData?.username) return;

    if (!hasSupabaseConfig) {
      setProfileError('Auth is not available right now. Please try again later.');
      return;
    }

    if (viewerSession?.user) {
      goToUnlockFlow();
      return;
    }

    setShowUnlockGate(true);
  }, [goToUnlockFlow, profileData?.username, viewerSession?.user]);

  const handleCopyProfileLink = useCallback(async () => {
    if (!profileShareUrl) return;
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(profileShareUrl);
      } else {
        const fallbackInput = document.createElement('textarea');
        fallbackInput.value = profileShareUrl;
        fallbackInput.setAttribute('readonly', '');
        fallbackInput.style.position = 'fixed';
        fallbackInput.style.opacity = '0';
        document.body.appendChild(fallbackInput);
        fallbackInput.focus();
        fallbackInput.select();
        document.execCommand('copy');
        document.body.removeChild(fallbackInput);
      }
      setCopyFeedback('Profile link copied.');
    } catch (_error) {
      setCopyFeedback('Could not copy link.');
    }
  }, [profileShareUrl]);

  const handleAuthSuccess = useCallback(() => {
    setShowAuthModal(false);
    setShowUnlockGate(false);
    goToUnlockFlow();
  }, [goToUnlockFlow]);

  useEffect(() => {
    if (!copyFeedback) return undefined;
    const timeoutId = window.setTimeout(() => setCopyFeedback(''), 2200);
    return () => window.clearTimeout(timeoutId);
  }, [copyFeedback]);

  useEffect(() => {
    let mounted = true;

    if (!hasSupabaseConfig) {
      setCheckingViewerSession(false);
      return undefined;
    }

    const initSession = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (!mounted) return;
        setViewerSession(session);
      } catch (error) {
        if (!mounted || isAbortError(error)) return;
        // eslint-disable-next-line no-console
        console.error('Failed to load viewer session:', error);
      } finally {
        if (mounted) {
          setCheckingViewerSession(false);
        }
      }
    };

    void initSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mounted) return;
      setViewerSession(session || null);
      setCheckingViewerSession(false);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    const loadProfile = async () => {
      setLoadingProfile(true);
      setProfileError('');

      if (!username) {
        setProfileData(null);
        setProfileError('Missing profile username in URL.');
        setLoadingProfile(false);
        return;
      }

      if (!hasSupabaseConfig) {
        setProfileData(null);
        setProfileError('Profile service is not configured.');
        setLoadingProfile(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('credentials')
          .select('id, username, avatar_url, gender, user_rate, chat_preference, user_rating, total_rating, rating_count')
          .eq('username', username)
          .maybeSingle();

        if (!mounted) return;

        if (error) {
          if (isProfileAccessError(error)) {
            setProfileData(createFallbackPublicProfile(username));
            setProfileError('');
            return;
          }
          setProfileData(null);
          setProfileError('Could not load this profile right now.');
          return;
        }

        if (!data) {
          const { data: caseInsensitiveMatch, error: caseInsensitiveError } = await supabase
            .from('credentials')
            .select('id, username, avatar_url, gender, user_rate, chat_preference, user_rating, total_rating, rating_count')
            .ilike('username', username)
            .limit(1)
            .maybeSingle();

          if (!mounted) return;

          if (caseInsensitiveError) {
            if (isProfileAccessError(caseInsensitiveError)) {
              setProfileData(createFallbackPublicProfile(username));
              setProfileError('');
              return;
            }
            setProfileData(null);
            setProfileError('Could not load this profile right now.');
            return;
          }

          if (!caseInsensitiveMatch) {
            // On many RLS setups, anonymous profile reads return zero rows instead of errors.
            // Keep profile pages publicly accessible and let unlock flow validate after auth.
            setProfileData(createFallbackPublicProfile(username));
            setProfileError('');
            return;
          }

          setProfileData(caseInsensitiveMatch);
          return;
        }

        setProfileData(data);
      } catch (error) {
        if (!mounted || isAbortError(error)) return;
        // eslint-disable-next-line no-console
        console.error('Failed to fetch public profile:', error);
        if (isProfileAccessError(error)) {
          setProfileData(createFallbackPublicProfile(username));
          setProfileError('');
        } else {
          setProfileData(null);
          setProfileError('Could not load this profile right now.');
        }
      } finally {
        if (mounted) {
          setLoadingProfile(false);
        }
      }
    };

    void loadProfile();

    return () => {
      mounted = false;
    };
  }, [username]);

  const genderIndicator = getGenderIndicator(profileData?.gender);

  useEffect(() => {
    const displayName = profileData?.username || username || 'Profile';
    document.title = `@${displayName} | Unlock Profile`;

    const descriptionTag = document.querySelector('meta[name="description"]');
    if (descriptionTag) {
      descriptionTag.setAttribute(
        'content',
        `Unlock the profile of @${displayName} and request a paid 24-hour fan chat on Unlock.`
      );
    }

    const robotsTag = document.querySelector('meta[name="robots"]');
    if (robotsTag) {
      robotsTag.setAttribute('content', 'index, follow');
    }

    const canonicalTag = document.querySelector('link[rel="canonical"]');
    if (canonicalTag && typeof window !== 'undefined') {
      canonicalTag.setAttribute('href', `${window.location.origin}/u/${encodeURIComponent(displayName)}`);
    }
  }, [profileData?.username, username]);

  return (
    <div className="public-profile-shell">
      <header className="public-profile-topbar">
        <div className="container public-profile-topbar-inner">
          <Link className="public-profile-brand" to="/">
            <span className="public-profile-logo-wrap">
              <img src={lockImage} alt="Unlock logo" className="logo-img" />
            </span>
            <span className="public-profile-brand-text">Unlock</span>
          </Link>
          <div className="public-profile-topbar-actions">
            <Link to="/" className="public-profile-topbar-link">
              Home
            </Link>
            {!viewerSession?.user ? (
              <button
                type="button"
                className="public-profile-topbar-btn"
                onClick={() => setShowAuthModal(true)}
                disabled={checkingViewerSession || !hasSupabaseConfig}
              >
                Sign in
              </button>
            ) : null}
          </div>
        </div>
      </header>

      <main className="public-profile-main">
        <div className="container public-profile-main-inner">
          {loadingProfile ? <p className="dashboard-muted">Loading profile...</p> : null}

          {!loadingProfile && profileError ? (
            <div className="public-profile-error-card">
              <h2>Profile Unavailable</h2>
              <p>{profileError}</p>
              <Link to="/" className="public-profile-back-link">
                Back to Unlock home
              </Link>
            </div>
          ) : null}

          {!loadingProfile && !profileError && profileData ? (
            <article className="public-profile-card">
              <div className="public-profile-hero">
                <div className="public-profile-avatar-wrap">
                  {profileData.avatar_url ? (
                    <img
                      src={profileData.avatar_url}
                      alt={`${profileData.username} avatar`}
                      className="public-profile-avatar"
                    />
                  ) : (
                    <div className="public-profile-avatar-fallback">
                      {getAvatarInitial(profileData.username)}
                    </div>
                  )}
                </div>
                <div className="public-profile-identity">
                  <h1>@{profileData.username}</h1>
                  <p>24-hour fan chat profile</p>
                </div>
                <div className="public-profile-pills">
                  <span className="public-profile-pill">{profileData.user_rate || 100} EGP / 24h</span>
                  <span className="public-profile-pill">{ratingLabel}</span>
                  {genderIndicator ? (
                    <span className={`public-profile-pill public-profile-gender ${genderIndicator.className}`}>
                      <span aria-hidden="true">{genderIndicator.symbol}</span>
                      {genderIndicator.label}
                    </span>
                  ) : null}
                  <span
                    className={`public-profile-pill ${
                      profileData.chat_preference === 'none' ? 'public-profile-pill-muted' : ''
                    }`}
                  >
                    {getAvailabilityLabel(profileData.chat_preference || 'both')}
                  </span>
                </div>
              </div>

              <div className="public-profile-cta-row">
                <button type="button" className="public-profile-unlock-btn" onClick={handleUnlockClick}>
                  Unlock 24h Chat
                </button>
                <button type="button" className="public-profile-share-btn" onClick={handleCopyProfileLink}>
                  {copyFeedback || 'Copy Profile Link'}
                </button>
              </div>

            </article>
          ) : null}
        </div>
      </main>

      {showUnlockGate && profileData ? (
        <div className="profile-auth-gate-overlay" onClick={() => setShowUnlockGate(false)}>
          <div className="profile-auth-gate-modal" onClick={(event) => event.stopPropagation()}>
            <h3>Sign in to unlock @{profileData.username}</h3>
            <p>Create an account or log in, then we will continue directly to the unlock flow.</p>
            <div className="profile-auth-gate-actions">
              <button
                type="button"
                className="settings-primary-btn"
                onClick={() => {
                  setShowUnlockGate(false);
                  setShowAuthModal(true);
                }}
              >
                Sign in / Sign up
              </button>
              <button type="button" className="settings-secondary-btn" onClick={() => setShowUnlockGate(false)}>
                Maybe later
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} onLoginSuccess={handleAuthSuccess} />
    </div>
  );
}

export default PublicProfilePage;
